/**
 * Sorts an array of objects by a specific key, with optional validity-based prioritization and value order.
 *
 * @template T
 * @param {T[]} array - The array of objects to sort.
 * @param {string} key - The key to sort by. Supports nested keys using dot notation (e.g., "user.email").
 * @param {Object} [options] - Optional sorting configuration.
 * @param {(value: any) => boolean} [options.isValidFn=null] - Optional function to determine if a value is valid.
 * @param {boolean} [options.validOnTop=true] - If true, valid entries are sorted to the top; otherwise to the bottom.
 * @param {"asc" | "desc"} [options.order="asc"] - Sort order for values (ascending or descending).
 *
 * @returns {T[]} - A new sorted array based on validity and value.
 *
 * @example
 * // Simple value sort
 * dynamicSmartSort(data, "name", { order: "asc" });
 *
 * @example
 * // Sort by email with valid emails pushed to bottom
 * dynamicSmartSort(data, "email", {
 *   isValidFn: (val) => val && val !== "email_not_unlocked@domain.com",
 *   validOnTop: false,
 *   order: "asc"
 * });
 */
export const dynamicSmartSort = (
  array,
  key,
  options = {
    isValidFn: null,         // Optional: (val) => boolean
    validOnTop: true,        // Optional: push valid items to top
    order: null,            // 'asc' or 'desc'
  }
) => {
  const getValue = (obj, path) =>
    path.split(".").reduce((acc, part) => acc?.[part], obj);

  return [...array].sort((a, b) => {
    const aVal = getValue(a, key);
    const bVal = getValue(b, key);

    if (typeof options.isValidFn === "function") {
      const aValid = options.isValidFn(aVal);
      const bValid = options.isValidFn(bVal);

      if (aValid !== bValid) {
        return options.validOnTop
          ? bValid - aValid
          : aValid - bValid;
      }
    }

    if (aVal === bVal) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (options.order!=null) {
            if (aVal < bVal) return options.order === "asc" ? -1 : 1;
    if (aVal > bVal) return options.order === "asc" ? 1 : -1;
    }



    return 0;
  });
};
